/**
 * Site renderer: SiteContent → complete static pages. Deterministic —
 * identical input yields byte-identical output. Public output carries
 * zero engine JavaScript; the only <script> is Schema.org JSON-LD.
 */
import { escapeHtml, safeUrl } from './escape.js';
import { renderHead, type HeadOptions } from './head.js';
import { rendererFor } from './patterns/index.js';
import type { PageT, SectionT, SiteContentT } from '../schema/index.js';

export type RenderOptions = HeadOptions;

export interface RenderedSite {
  /** Output files keyed by path ("/index.html", "/menu/index.html"). */
  files: Record<string, string>;
}

interface HtmlAttribute {
  name: string;
  value: string | null;
  end: number;
}

function isHtmlSpace(char: string | undefined): boolean {
  return char === ' ' || char === '\n' || char === '\r' || char === '\t' || char === '\f';
}

function scanTagAttributes(tag: string): HtmlAttribute[] {
  const attributes: HtmlAttribute[] = [];
  let cursor = 1;
  while (cursor < tag.length && !isHtmlSpace(tag[cursor]) && tag[cursor] !== '>') cursor += 1;
  while (cursor < tag.length) {
    while (isHtmlSpace(tag[cursor]) || tag[cursor] === '/') cursor += 1;
    if (cursor >= tag.length || tag[cursor] === '>') break;
    const nameStart = cursor;
    while (cursor < tag.length && !isHtmlSpace(tag[cursor]) && tag[cursor] !== '=' && tag[cursor] !== '>' && tag[cursor] !== '/') cursor += 1;
    const name = tag.slice(nameStart, cursor).toLowerCase();
    while (isHtmlSpace(tag[cursor])) cursor += 1;
    let value: string | null = null;
    if (tag[cursor] === '=') {
      cursor += 1;
      while (isHtmlSpace(tag[cursor])) cursor += 1;
      const quote = tag[cursor];
      if (quote === '"' || quote === "'") {
        cursor += 1;
        const valueStart = cursor;
        while (cursor < tag.length && tag[cursor] !== quote) cursor += 1;
        value = tag.slice(valueStart, cursor);
        if (tag[cursor] === quote) cursor += 1;
      } else {
        const valueStart = cursor;
        while (cursor < tag.length && !isHtmlSpace(tag[cursor]) && tag[cursor] !== '>') cursor += 1;
        value = tag.slice(valueStart, cursor);
      }
    }
    if (name !== '') attributes.push({ name, value, end: cursor });
  }
  return attributes;
}

function tagEnd(html: string, start: number): number {
  let quote = '';
  for (let cursor = start + 1; cursor < html.length; cursor += 1) {
    const char = html[cursor]!;
    if (quote !== '') {
      if (char === quote) quote = '';
    } else if (char === '"' || char === "'") {
      quote = char;
    } else if (char === '>') {
      return cursor;
    }
  }
  return -1;
}

function qualifyFreeformTag(tag: string, section: Extract<SectionT, { kind: 'freeform' }>): string {
  if (tag.startsWith('</') || tag.startsWith('<!') || tag.startsWith('<?')) return tag;
  const attributes = scanTagAttributes(tag);
  const edit = attributes.find((attribute) => attribute.name === 'data-edit' && attribute.value !== null);
  if (!edit) return tag;
  const manifest = section.editableManifest.find((entry) => escapeHtml(entry.fieldId) === edit.value);
  if (!manifest) return tag;
  const names = new Set(attributes.map((attribute) => attribute.name));
  let qualifiers = '';
  if (!names.has('data-veldon-section')) qualifiers += ` data-veldon-section="${escapeHtml(section.id)}"`;
  if (!names.has('data-veldon-field')) qualifiers += ` data-veldon-field="${escapeHtml(manifest.fieldId)}"`;
  return qualifiers === '' ? tag : `${tag.slice(0, edit.end)}${qualifiers}${tag.slice(edit.end)}`;
}

function qualifyFreeformMarkers(section: Extract<SectionT, { kind: 'freeform' }>): string {
  const html = section.html;
  const parts: string[] = [];
  let cursor = 0;
  while (cursor < html.length) {
    const start = html.indexOf('<', cursor);
    if (start < 0) break;
    const end = tagEnd(html, start);
    if (end < 0) break;
    parts.push(html.slice(cursor, start), qualifyFreeformTag(html.slice(start, end + 1), section));
    cursor = end + 1;
  }
  if (parts.length === 0) return html;
  parts.push(html.slice(cursor));
  return parts.join('');
}

function renderSection(section: SectionT): { html: string; css: string } {
  if (section.kind === 'freeform') {
    // Freeform html is the COMPLETE section markup (validated: carries the
    // anchor id and data-edit markers, no scripts). Inserted verbatim.
    return { html: qualifyFreeformMarkers(section), css: section.css ?? '' };
  }
  return { html: rendererFor(section.type)(section), css: '' };
}

function renderNav(content: SiteContentT): string {
  if (content.nav.items.length === 0) return '';
  const items = content.nav.items
    .map((item) => `      <li><a href="${safeUrl(item.target)}">${escapeHtml(item.label)}</a></li>`)
    .join('\n');
  return [
    '  <header class="ve-header">',
    `    <a class="ve-header__brand" href="/">${escapeHtml(content.site.name)}</a>`,
    `    <nav class="ve-header__nav" aria-label="${escapeHtml(content.site.name)}">`,
    '      <ul>',
    items,
    '      </ul>',
    '    </nav>',
    '  </header>',
  ].join('\n');
}

export function renderPage(content: SiteContentT, page: PageT, options: RenderOptions = {}): string {
  const rendered = page.sections.map((section) => renderSection(section));
  // A page that carries its own header section (bespoke sites) suppresses
  // the shell header — never render two site headers.
  const hasOwnHeader = page.sections.some((section) => section.anchor === 'header');
  const sectionCss = rendered
    .map((part) => part.css)
    .filter(Boolean)
    .join('\n');

  // Sections whose html roots are <header>/<footer> elements live outside
  // <main> — before and after it respectively.
  const bodyBefore: string[] = [];
  const bodyMain: string[] = [];
  const bodyAfter: string[] = [];
  for (const part of rendered) {
    const root = part.html.trimStart().toLowerCase();
    if (root.startsWith('<header')) bodyBefore.push(part.html);
    else if (root.startsWith('<footer')) bodyAfter.push(part.html);
    else bodyMain.push(part.html);
  }

  return [
    '<!doctype html>',
    `<html lang="${escapeHtml(content.site.locale)}">`,
    '<head>',
    renderHead(content, page, sectionCss, options),
    '</head>',
    '<body>',
    hasOwnHeader ? '' : renderNav(content),
    ...bodyBefore,
    '  <main>',
    ...bodyMain,
    '  </main>',
    ...bodyAfter,
    '</body>',
    '</html>',
    '',
  ].join('\n');
}

export function renderSite(content: SiteContentT, options: RenderOptions = {}): RenderedSite {
  const files: Record<string, string> = {};
  for (const page of content.pages) {
    const filePath = page.path === '/' ? '/index.html' : `${page.path}/index.html`;
    files[filePath] = renderPage(content, page, options);
  }
  return { files };
}
